/**
 * Builds CreateTaskRequest for the new Create Task modal (POST /task/v1/tasks).
 * Does not modify any existing formatters.
 */
import { TaskEntity } from 'app/models/Tasks/Tasks';
import {
  ICreateTaskRequest,
  RootTaskCategory,
  TaskPriorityV1,
} from 'app/models/Tasks/CreateTaskV1';

export interface IToRecipientForRequest {
  object?: { empId?: number; label?: string; role?: string };
  label?: string;
}

export interface IFromOptionForRequest {
  id?: number | string;
  coveredEmployeeId?: number;
  coverageDetails?: { coveringEmployeeId?: number; coveredEmployeeId?: number };
}

export interface IBuildCreateTaskRequestParams {
  subject: string;
  description: string;
  category: string;
  followUpDate: string;
  priority: string;
  flagChecked: boolean;
  optionApprovalChecked: boolean;
  followUpChecked: boolean;
  toRecipients: IToRecipientForRequest[];
  ccRecipients: IToRecipientForRequest[];
  selectedFromOption: IFromOptionForRequest | null;
  getAllIds: {
    placementId?: number;
    candidateId?: number;
    orderId?: number;
    facilityId?: number;
    unitId?: number;
    companyId?: number;
  };
  container: number;
  originalTaskId?: number;
}

/** Returns due date as YYYY-MM-DD (date only, no time) for the API. */
function parseToDateOnly(dateStr: string): string | undefined {
  if (!dateStr || !dateStr.trim()) return undefined;

  const trimmed = dateStr.trim();
  const parsed = new Date(trimmed);
  if (isNaN(parsed.getTime())) return trimmed;

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getContextIds(
  container: number,
  getAllIds: IBuildCreateTaskRequestParams['getAllIds'],
): Pick<ICreateTaskRequest, 'travelerId' | 'placementId' | 'orderId' | 'facilityId'> {
  const placementId = getAllIds?.placementId != null ? Number(getAllIds.placementId) : undefined;
  const candidateId = getAllIds?.candidateId != null ? Number(getAllIds.candidateId) : undefined;
  const orderId = getAllIds?.orderId != null ? Number(getAllIds.orderId) : undefined;
  const facilityId = getAllIds?.facilityId != null ? Number(getAllIds.facilityId) : undefined;

  switch (container) {
    case TaskEntity.CANDIDATES:
      return candidateId && candidateId > 0 ? { travelerId: candidateId } : {};
    case TaskEntity.PLACEMENTS:
      return placementId && placementId > 0 ? { placementId } : {};
    case TaskEntity.ORDERS:
      return orderId && orderId > 0 ? { orderId } : {};
    case TaskEntity.FACILITIES:
      return facilityId && facilityId > 0 ? { facilityId } : {};
    default:
      return (
        (candidateId && candidateId > 0 && { travelerId: candidateId }) ||
        (placementId && placementId > 0 && { placementId }) ||
        (orderId && orderId > 0 && { orderId }) ||
        (facilityId && facilityId > 0 && { facilityId }) ||
        {}
      );
  }
}

function isRootTaskCategoryValue(value: number): value is RootTaskCategory {
  return Object.values(RootTaskCategory)
    .filter((v): v is RootTaskCategory => typeof v === 'number')
    .includes(value as RootTaskCategory);
}

function normalizeCategoryText(value: string): string {
  return (value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
    .trim();
}

function parseRootCategory(category: string): RootTaskCategory {
  const categoryNum = Number(category);
  if (Number.isInteger(categoryNum) && isRootTaskCategoryValue(categoryNum)) {
    return categoryNum;
  }

  const normalizedInput = normalizeCategoryText(category);
  if (!normalizedInput) return RootTaskCategory.General;

  const enumEntries = Object.entries(RootTaskCategory).filter(([, value]) => typeof value === 'number') as Array<
    [string, number]
  >;
  const match = enumEntries.find(([key]) => normalizeCategoryText(key) === normalizedInput);
  return (match?.[1] as RootTaskCategory) ?? RootTaskCategory.General;
}

export function buildCreateTaskRequestFromNewModal(
  params: IBuildCreateTaskRequestParams,
): ICreateTaskRequest {
  const {
    subject,
    description,
    category,
    followUpDate,
    priority,
    flagChecked,
    optionApprovalChecked,
    followUpChecked,
    toRecipients,
    ccRecipients,
    selectedFromOption,
    getAllIds,
    container,
    originalTaskId,
  } = params;

  const toEmployeeIds = (toRecipients || [])
    .map(r => r?.object?.empId)
    .filter((id): id is number => id != null && id > 0);
  const ccEmployeeIds = (ccRecipients || [])
    .map(r => r?.object?.empId)
    .filter((id): id is number => id != null && id > 0);

  const rootCategory: RootTaskCategory = parseRootCategory(category);

  const priorityMap: Record<string, TaskPriorityV1> = {
    High: TaskPriorityV1.High,
    Normal: TaskPriorityV1.Normal,
    Low: TaskPriorityV1.Low,
  };
  const taskPriority = priorityMap[priority] ?? TaskPriorityV1.Normal;

  const dueDateOnly = parseToDateOnly(followUpDate);
  const followUpDateOnly = followUpChecked ? dueDateOnly : undefined;

  const sendOnBehalfOfEmployeeId =
    selectedFromOption?.coverageDetails != null && selectedFromOption?.coveredEmployeeId != null
      ? selectedFromOption.coveredEmployeeId
      : undefined;

  const contextIds = getContextIds(container, getAllIds);

  return {
    ...contextIds,
    dueDate: dueDateOnly as string,
    toEmployeeIds,
    ccEmployeeIds: ccEmployeeIds.length > 0 ? ccEmployeeIds : undefined,
    category: rootCategory,
    priority: taskPriority,
    subject: (subject || '').trim().slice(0, 200),
    message: (description || '').trim().slice(0, 8000) || undefined,
    isFollowUp: followUpChecked,
    isOptionApproval: optionApprovalChecked,
    isFlagged: flagChecked,
    followUpDate: followUpDateOnly ?? undefined,
    sendOnBehalfOfEmployeeId: sendOnBehalfOfEmployeeId ?? undefined,
    originalTaskId: originalTaskId ?? undefined,
  };
}
